import { byNumericId } from './id-sort.ts';
import type { BuilderState } from './schema.ts';
import type { LoketTicketApiImport } from './parse-loket-ticket-api.ts';

export interface MergeSummary {
  ticketNamesAdded: number;
  ticketNamesUpdated: number;
  addonNamesAdded: number;
  addonNamesUpdated: number;
  newTicketIds: number;
  newAddonIds: number;
}

export function mergeLoketImport(
  state: BuilderState,
  parsed: LoketTicketApiImport,
): MergeSummary {
  const summary: MergeSummary = {
    ticketNamesAdded: 0,
    ticketNamesUpdated: 0,
    addonNamesAdded: 0,
    addonNamesUpdated: 0,
    newTicketIds: 0,
    newAddonIds: 0,
  };

  // Merge group id only if current state is empty
  if (!state.activeTicketGroupId && parsed.activeTicketGroupId) {
    state.activeTicketGroupId = parsed.activeTicketGroupId;
  }

  // Merge tickets with empty addon arrays only if missing
  for (const ticketId of Object.keys(parsed.activeTicketAddonMap)) {
    if (state.activeTicketAddonMap[ticketId] === undefined) {
      state.activeTicketAddonMap[ticketId] = [];
      summary.newTicketIds++;
    }
  }

  // Merge addons with empty category only if missing
  for (const addonId of Object.keys(parsed.addonCategoryById)) {
    if (state.addonCategoryById[addonId] === undefined) {
      state.addonCategoryById[addonId] = '';
      summary.newAddonIds++;
    }
  }

  // Merge names, preferring non-empty imported names
  for (const [ticketId, name] of Object.entries(parsed.ticketNamesById)) {
    if (name) {
      const had = state.ticketNamesById[ticketId];
      state.ticketNamesById[ticketId] = name;
      if (!had) summary.ticketNamesAdded++;
      else if (had !== name) summary.ticketNamesUpdated++;
    }
  }

  for (const [addonId, name] of Object.entries(parsed.addonNamesById)) {
    if (name) {
      const had = state.addonNamesById[addonId];
      state.addonNamesById[addonId] = name;
      if (!had) summary.addonNamesAdded++;
      else if (had !== name) summary.addonNamesUpdated++;
    }
  }

  // Set selected ticket to first imported if none selected
  if (!state.selectedTicketId) {
    const importedTicketIds = Object.keys(parsed.activeTicketAddonMap).sort(byNumericId);
    state.selectedTicketId = importedTicketIds[0] || null;
  }

  return summary;
}
